import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, Edit2, Check, X, Archive, Link2, GripVertical, Settings2 } from 'lucide-react'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { projectsApi } from '../api/projects'
import { usersApi } from '../api/users'
import { Button } from '../components/common/Button'
import { Avatar } from '../components/common/Avatar'
import { useAuthStore } from '../store/authStore'
import { copyTextToClipboard } from '../lib/clipboard'
import { invalidateIssueViews, invalidateProjectConfigurationViews } from '../lib/queryInvalidation'
import { formatRoleLabel, hasFullAccess } from '../lib/roles'
import type { IssueStatus, Label, Component, Milestone, User, Project } from '../types'

type SettingsTab = 'members' | 'statuses' | 'labels' | 'components' | 'milestones'

const STATUS_CATEGORIES = [
  { value: 'TODO', label: 'To Do' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'DONE', label: 'Done' },
] as const

const MILESTONE_STATUSES = ['PLANNED', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'] as const

const PRESET_COLORS = ['#6366f1', '#ec4899', '#f97316', '#22c55e', '#14b8a6', '#eab308', '#ef4444', '#94a3b8']

function ColorPicker({ value, onChange }: { value: string; onChange: (c: string) => void }) {
  return (
    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
      {PRESET_COLORS.map((c) => (
        <button
          key={c}
          onClick={() => onChange(c)}
          style={{
            width: 22, height: 22, borderRadius: '50%', background: c, border: 'none', cursor: 'pointer',
            outline: value === c ? `2px solid ${c}` : '2px solid transparent',
            outlineOffset: '2px',
          }}
        />
      ))}
    </div>
  )
}

function InlineEdit({
  value,
  onSave,
  onDelete,
  extra,
}: {
  value: string
  onSave: (newVal: string) => void
  onDelete: () => void
  extra?: React.ReactNode
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)

  if (editing) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1 }}>
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          style={{ flex: 1, padding: '4px 8px', fontSize: '13px', border: '1px solid #6366f1', borderRadius: '5px', outline: 'none' }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { onSave(draft); setEditing(false) }
            if (e.key === 'Escape') setEditing(false)
          }}
        />
        <button onClick={() => { onSave(draft); setEditing(false) }} style={iconBtn}><Check size={13} /></button>
        <button onClick={() => setEditing(false)} style={iconBtn}><X size={13} /></button>
      </div>
    )
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
      <span style={{ fontSize: '13.5px', color: '#1e293b', fontWeight: 500 }}>{value}</span>
      {extra}
      <button onClick={() => { setDraft(value); setEditing(true) }} style={{ ...iconBtn, marginLeft: 'auto' }}><Edit2 size={12} /></button>
      <button onClick={onDelete} style={{ ...iconBtn, color: '#ef4444' }}><Trash2 size={12} /></button>
    </div>
  )
}

const iconBtn: React.CSSProperties = {
  background: 'none', border: 'none', cursor: 'pointer', color: '#64748b',
  display: 'flex', padding: '3px', borderRadius: '4px',
}

function SortableStatusRow({
  status,
  onSave,
  onDelete,
}: {
  status: IssueStatus
  onSave: (name: string) => void
  onDelete: () => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: status.id })

  const style: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 16px',
    borderBottom: '1px solid #f1f5f9',
    background: isDragging ? '#f8fafc' : '#ffffff',
    opacity: isDragging ? 0.7 : 1,
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div ref={setNodeRef} style={style}>
      <span
        {...attributes}
        {...listeners}
        style={{ cursor: 'grab', color: '#cbd5e1', display: 'flex', flexShrink: 0 }}
        title="Drag to reorder"
      >
        <GripVertical size={15} />
      </span>
      <span style={{ width: 12, height: 12, borderRadius: '50%', background: status.color, display: 'inline-block', flexShrink: 0 }} />
      <InlineEdit
        value={status.name}
        onSave={onSave}
        onDelete={onDelete}
        extra={
          <span style={{ fontSize: '11px', color: '#94a3b8', background: '#f1f5f9', padding: '1px 6px', borderRadius: '4px' }}>
            {status.category}
          </span>
        }
      />
    </div>
  )
}

const inputStyle: React.CSSProperties = {
  padding: '7px 10px', fontSize: '13px', border: '1px solid #e2e8f0',
  borderRadius: '6px', outline: 'none', boxSizing: 'border-box',
}

function EditProjectModal({
  isOpen,
  onClose,
  project,
  onSave,
  isSaving,
}: {
  isOpen: boolean
  onClose: () => void
  project: Project
  onSave: (data: { name: string; description: string; color: string; slackChannelId: string; showOnDashboard: boolean }) => void
  isSaving: boolean
}) {
  const [name, setName] = React.useState(project.name)
  const [description, setDescription] = React.useState(project.description || '')
  const [color, setColor] = React.useState(project.color || PRESET_COLORS[0])
  const [slackChannelId, setSlackChannelId] = React.useState(project.slackChannelId || '')
  const [showOnDashboard, setShowOnDashboard] = React.useState(project.showOnDashboard || false)

  React.useEffect(() => {
    if (isOpen) {
      setName(project.name)
      setDescription(project.description || '')
      setColor(project.color || PRESET_COLORS[0])
      setSlackChannelId(project.slackChannelId || '')
      setShowOnDashboard(project.showOnDashboard || false)
    }
  }, [isOpen, project])

  return (
    <div
      style={{
        display: isOpen ? 'flex' : 'none',
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.4)',
        alignItems: 'center', justifyContent: 'center',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{ background: '#fff', borderRadius: '12px', padding: '28px', width: '480px', maxWidth: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <h2 style={{ margin: '0 0 20px', fontSize: '18px', fontWeight: 700, color: '#1e293b' }}>Edit Project</h2>

        <div style={{ marginBottom: '14px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#374151', marginBottom: '4px' }}>Project Name *</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{ ...inputStyle, width: '100%' }}
            placeholder="e.g. Website Redesign"
          />
        </div>

        <div style={{ marginBottom: '14px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#374151', marginBottom: '4px' }}>Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            style={{ ...inputStyle, width: '100%', resize: 'vertical', fontFamily: 'inherit' }}
            placeholder="Optional description..."
          />
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#374151', marginBottom: '4px' }}>Slack Channel ID</label>
          <input
            value={slackChannelId}
            onChange={(e) => setSlackChannelId(e.target.value)}
            style={{ ...inputStyle, width: '100%' }}
            placeholder="e.g. C08ABC12345"
          />
        </div>

        <label
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px',
            marginBottom: '24px',
            padding: '12px',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            background: '#f8fafc',
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={showOnDashboard}
            onChange={(e) => setShowOnDashboard(e.target.checked)}
            style={{ marginTop: '2px' }}
          />
          <span>
            <span style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#1f2937' }}>
              Show in Project Values
            </span>
            <span style={{ display: 'block', marginTop: '2px', fontSize: '12px', color: '#64748b', lineHeight: 1.4 }}>
              Include this project on the employee estimate vs actual dashboard.
            </span>
          </span>
        </label>

        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#374151', marginBottom: '8px' }}>Color</label>
          <ColorPicker value={color} onChange={setColor} />
        </div>

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={isSaving}
            disabled={!name.trim()}
            onClick={() => onSave({ name: name.trim(), description, color, slackChannelId, showOnDashboard })}
          >
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  )
}

export default function ProjectSettingsPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { user } = useAuthStore()
  const [tab, setTab] = useState<SettingsTab>('members')
  const [copyState, setCopyState] = useState<'idle' | 'copied'>('idle')
  const [editProjectOpen, setEditProjectOpen] = useState(false)
  const canManageProject = hasFullAccess(user?.role)

  const updateProjectMutation = useMutation({
    mutationFn: (data: { name: string; description: string; color: string; slackChannelId: string; showOnDashboard: boolean }) =>
      projectsApi.updateProject(projectId!, data),
    onSuccess: async () => {
      await invalidateProjectConfigurationViews(qc, projectId!)
      setEditProjectOpen(false)
    },
  })

  const { data: projectData } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.getProject(projectId!),
    enabled: !!projectId,
  })

  // ── Members ──
  const { data: membersData } = useQuery({
    queryKey: ['members', projectId],
    queryFn: () => projectsApi.listMembers(projectId!),
    enabled: !!projectId,
  })
  const { data: allUsersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.listUsers(),
    enabled: tab === 'members',
  })
  const [addMemberUserId, setAddMemberUserId] = useState('')
  const addMemberMutation = useMutation({
    mutationFn: () => projectsApi.addMember(projectId!, addMemberUserId),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['members', projectId] }),
        qc.invalidateQueries({ queryKey: ['project', projectId] }),
      ])
      setAddMemberUserId('')
    },
  })
  const removeMemberMutation = useMutation({
    mutationFn: (userId: string) => projectsApi.removeMember(projectId!, userId),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['members', projectId] }),
        qc.invalidateQueries({ queryKey: ['project', projectId] }),
      ])
    },
  })
  const archiveProjectMutation = useMutation({
    mutationFn: () => projectsApi.archiveProject(projectId!),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['projects'] }),
        qc.invalidateQueries({ queryKey: ['project', projectId] }),
      ])
      navigate('/my-issues')
    },
  })

  // ── Statuses ──
  const { data: statusesData } = useQuery({
    queryKey: ['statuses', projectId],
    queryFn: () => projectsApi.listStatuses(projectId!),
    enabled: !!projectId,
  })
  const [newStatus, setNewStatus] = useState({ name: '', color: PRESET_COLORS[0], category: 'TODO' as IssueStatus['category'] })
  const createStatusMutation = useMutation({
    mutationFn: () => projectsApi.createStatus(projectId!, { ...newStatus, position: (statusesData?.data?.data?.length ?? 0) }),
    onSuccess: async () => {
      await invalidateProjectConfigurationViews(qc, projectId!)
      setNewStatus({ name: '', color: PRESET_COLORS[0], category: 'TODO' })
    },
  })
  const updateStatusMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<IssueStatus> }) => projectsApi.updateStatus(projectId!, id, data),
    onSuccess: async () => {
      await invalidateProjectConfigurationViews(qc, projectId!)
    },
  })
  const deleteStatusMutation = useMutation({
    mutationFn: (id: string) => projectsApi.deleteStatus(projectId!, id),
    onSuccess: async () => {
      await invalidateProjectConfigurationViews(qc, projectId!)
    },
  })

  // ── Labels ──
  const { data: labelsData } = useQuery({
    queryKey: ['labels', projectId],
    queryFn: () => projectsApi.listLabels(projectId!),
    enabled: !!projectId,
  })
  const [newLabel, setNewLabel] = useState({ name: '', color: PRESET_COLORS[0] })
  const createLabelMutation = useMutation({
    mutationFn: () => projectsApi.createLabel(projectId!, newLabel),
    onSuccess: async () => {
      await invalidateProjectConfigurationViews(qc, projectId!)
      setNewLabel({ name: '', color: PRESET_COLORS[0] })
    },
  })
  const deleteLabelMutation = useMutation({
    mutationFn: (id: string) => projectsApi.deleteLabel(projectId!, id),
    onSuccess: async () => {
      await invalidateProjectConfigurationViews(qc, projectId!)
    },
  })
  const updateLabelMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Label> }) => projectsApi.updateLabel(projectId!, id, data),
    onSuccess: async () => {
      await invalidateProjectConfigurationViews(qc, projectId!)
    },
  })

  // ── Components ──
  const { data: componentsData } = useQuery({
    queryKey: ['components', projectId],
    queryFn: () => projectsApi.listComponents(projectId!),
    enabled: !!projectId,
  })
  const [newComponent, setNewComponent] = useState('')
  const createComponentMutation = useMutation({
    mutationFn: () => projectsApi.createComponent(projectId!, { name: newComponent }),
    onSuccess: async () => {
      await invalidateProjectConfigurationViews(qc, projectId!)
      setNewComponent('')
    },
  })
  const deleteComponentMutation = useMutation({
    mutationFn: (id: string) => projectsApi.deleteComponent(projectId!, id),
    onSuccess: async () => {
      await invalidateProjectConfigurationViews(qc, projectId!)
    },
  })
  const updateComponentMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Component> }) => projectsApi.updateComponent(projectId!, id, data),
    onSuccess: async () => {
      await invalidateProjectConfigurationViews(qc, projectId!)
    },
  })

  // ── Milestones ──
  const { data: milestonesData } = useQuery({
    queryKey: ['milestones', projectId],
    queryFn: () => projectsApi.listMilestones(projectId!),
    enabled: !!projectId,
  })
  const [newMilestone, setNewMilestone] = useState({ name: '', status: 'PLANNED' as Milestone['status'] })
  const createMilestoneMutation = useMutation({
    mutationFn: () => projectsApi.createMilestone(projectId!, newMilestone),
    onSuccess: async () => {
      await invalidateProjectConfigurationViews(qc, projectId!)
      setNewMilestone({ name: '', status: 'PLANNED' })
    },
  })
  const deleteMilestoneMutation = useMutation({
    mutationFn: (id: string) => projectsApi.deleteMilestone(projectId!, id),
    onSuccess: async () => {
      await invalidateProjectConfigurationViews(qc, projectId!)
    },
  })

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  const handleStatusDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = statuses.findIndex((s) => s.id === active.id)
    const newIndex = statuses.findIndex((s) => s.id === over.id)
    const reordered = arrayMove(statuses, oldIndex, newIndex)
    reorderStatusesMutation.mutate(reordered.map((s) => s.id))
  }

  const reorderStatusesMutation = useMutation({
    mutationFn: (orderedIds: string[]) => projectsApi.reorderStatuses(projectId!, orderedIds),
    onSuccess: async () => {
      await invalidateProjectConfigurationViews(qc, projectId!)
    },
  })

  const members = membersData?.data?.data || []
  const allUsers: User[] = allUsersData?.data?.data || []
  const memberUserIds = new Set(members.map((m) => m.userId))
  const nonMembers = allUsers.filter((u) => !memberUserIds.has(u.id))
  const statuses = (statusesData?.data?.data || []).sort((a, b) => a.position - b.position)
  const labels = labelsData?.data?.data || []
  const components = componentsData?.data?.data || []
  const milestones = milestonesData?.data?.data || []
  const project = projectData?.data?.data

  const handleCopyLink = async () => {
    try {
      await copyTextToClipboard(window.location.href)
      setCopyState('copied')
      window.setTimeout(() => setCopyState('idle'), 2000)
    } catch {
      window.alert('Could not copy the project link from this browser.')
    }
  }

  const handleArchiveProject = () => {
    if (!project || archiveProjectMutation.isPending) return
    if (!window.confirm(`Archive "${project.name}"? This will hide it from the active project list.`)) {
      return
    }
    archiveProjectMutation.mutate()
  }

  const tabBtn = (t: SettingsTab, label: string) => (
    <button
      key={t}
      onClick={() => setTab(t)}
      style={{
        padding: '8px 16px',
        fontSize: '13px',
        fontWeight: 500,
        border: 'none',
        borderBottom: tab === t ? '2px solid #6366f1' : '2px solid transparent',
        background: 'none',
        color: tab === t ? '#6366f1' : '#64748b',
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  )

  const sectionCard = (children: React.ReactNode) => (
    <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '10px', overflow: 'hidden' }}>
      {children}
    </div>
  )

  const listRow = (children: React.ReactNode, key: string) => (
    <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 16px', borderBottom: '1px solid #f1f5f9' }}>
      {children}
    </div>
  )

  return (
    <>
      <div style={{ padding: '24px', width: '100%', boxSizing: 'border-box' }}>
      <h1 style={{ fontSize: '20px', fontWeight: 700, color: '#1e293b', margin: '0 0 20px' }}>
        Project Settings
      </h1>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '16px' }}>
        <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
          {project && (
            <>
              <div>
                <div style={{ fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>{project.name}</div>
                <div style={{ fontSize: '12px', color: '#94a3b8', fontFamily: 'monospace' }}>{project.identifier}</div>
              </div>
              {canManageProject && (
                <button
                  onClick={() => setEditProjectOpen(true)}
                  title="Edit project details"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '4px', borderRadius: '4px', display: 'flex' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#6366f1')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = '#94a3b8')}
                >
                  <Settings2 size={15} />
                </button>
              )}
            </>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <Button variant="secondary" size="sm" onClick={handleCopyLink}>
            <Link2 size={13} /> {copyState === 'copied' ? 'Link copied' : 'Copy link'}
          </Button>
          {canManageProject && (
            <Button
              variant="secondary"
              size="sm"
              loading={archiveProjectMutation.isPending}
              onClick={handleArchiveProject}
            >
              <Archive size={13} /> Archive project
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', marginBottom: '24px' }}>
        {tabBtn('members', 'Members')}
        {tabBtn('statuses', 'Statuses')}
        {tabBtn('labels', 'Labels')}
        {tabBtn('components', 'Components')}
        {tabBtn('milestones', 'Milestones')}
      </div>

      {/* ── Members ── */}
      {tab === 'members' && sectionCard(
        <>
          {members.map((m) => listRow(
            <>
              <Avatar user={m.user} size="sm" />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '13.5px', fontWeight: 500, color: '#1e293b' }}>{m.user.fullName}</div>
                <div style={{ fontSize: '11px', color: '#94a3b8' }}>{m.user.email} · {formatRoleLabel(m.user.role)}</div>
              </div>
              {canManageProject && (
                <button
                  onClick={() => removeMemberMutation.mutate(m.userId)}
                  style={{ ...iconBtn, color: '#ef4444' }}
                  title="Remove member"
                >
                  <Trash2 size={13} />
                </button>
              )}
            </>,
            m.id
          ))}
          {canManageProject && (
            <div style={{ padding: '12px 16px', display: 'flex', gap: '8px' }}>
              <select
                value={addMemberUserId}
                onChange={(e) => setAddMemberUserId(e.target.value)}
                style={{ ...inputStyle, flex: 1 }}
              >
                <option value="">Select user to add…</option>
                {nonMembers.map((u) => (
                  <option key={u.id} value={u.id}>{u.fullName} ({u.email})</option>
                ))}
              </select>
              <Button
                variant="primary"
                size="sm"
                disabled={!addMemberUserId}
                loading={addMemberMutation.isPending}
                onClick={() => addMemberMutation.mutate()}
              >
                <Plus size={13} /> Add
              </Button>
            </div>
          )}
        </>
      )}

      {/* ── Statuses ── */}
      {tab === 'statuses' && sectionCard(
        <>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleStatusDragEnd}>
            <SortableContext items={statuses.map((s) => s.id)} strategy={verticalListSortingStrategy}>
              {statuses.map((s) => (
                <SortableStatusRow
                  key={s.id}
                  status={s}
                  onSave={(name) => updateStatusMutation.mutate({ id: s.id, data: { name } })}
                  onDelete={() => deleteStatusMutation.mutate(s.id)}
                />
              ))}
            </SortableContext>
          </DndContext>
          <div style={{ padding: '12px 16px', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <input value={newStatus.name} onChange={(e) => setNewStatus(p => ({ ...p, name: e.target.value }))}
              placeholder="Status name" style={{ ...inputStyle, flex: '1 1 160px' }} />
            <select value={newStatus.category} onChange={(e) => setNewStatus(p => ({ ...p, category: e.target.value as IssueStatus['category'] }))}
              style={{ ...inputStyle }}>
              {STATUS_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
            <ColorPicker value={newStatus.color} onChange={(c) => setNewStatus(p => ({ ...p, color: c }))} />
            <Button variant="primary" size="sm" disabled={!newStatus.name.trim()} loading={createStatusMutation.isPending}
              onClick={() => createStatusMutation.mutate()}>
              <Plus size={13} /> Add
            </Button>
          </div>
        </>
      )}

      {/* ── Labels ── */}
      {tab === 'labels' && sectionCard(
        <>
          {labels.map((l) => listRow(
            <>
              <span style={{ width: 12, height: 12, borderRadius: '50%', background: l.color, display: 'inline-block', flexShrink: 0 }} />
              <InlineEdit
                value={l.name}
                onSave={(name) => updateLabelMutation.mutate({ id: l.id, data: { name } })}
                onDelete={() => deleteLabelMutation.mutate(l.id)}
              />
            </>,
            l.id
          ))}
          <div style={{ padding: '12px 16px', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <input value={newLabel.name} onChange={(e) => setNewLabel(p => ({ ...p, name: e.target.value }))}
              placeholder="Label name" style={{ ...inputStyle, flex: '1 1 200px' }} />
            <ColorPicker value={newLabel.color} onChange={(c) => setNewLabel(p => ({ ...p, color: c }))} />
            <Button variant="primary" size="sm" disabled={!newLabel.name.trim()} loading={createLabelMutation.isPending}
              onClick={() => createLabelMutation.mutate()}>
              <Plus size={13} /> Add
            </Button>
          </div>
        </>
      )}

      {/* ── Components ── */}
      {tab === 'components' && sectionCard(
        <>
          {components.map((c) => listRow(
            <InlineEdit
              value={c.name}
              onSave={(name) => updateComponentMutation.mutate({ id: c.id, data: { name } })}
              onDelete={() => deleteComponentMutation.mutate(c.id)}
            />,
            c.id
          ))}
          <div style={{ padding: '12px 16px', display: 'flex', gap: '8px' }}>
            <input value={newComponent} onChange={(e) => setNewComponent(e.target.value)}
              placeholder="Component name" style={{ ...inputStyle, flex: 1 }} />
            <Button variant="primary" size="sm" disabled={!newComponent.trim()} loading={createComponentMutation.isPending}
              onClick={() => createComponentMutation.mutate()}>
              <Plus size={13} /> Add
            </Button>
          </div>
        </>
      )}

      {/* ── Milestones ── */}
      {tab === 'milestones' && sectionCard(
        <>
          {milestones.map((m) => listRow(
            <>
              <InlineEdit
                value={m.name}
                onSave={() => {}}
                onDelete={() => deleteMilestoneMutation.mutate(m.id)}
                extra={
                  <span style={{ fontSize: '11px', color: '#94a3b8', background: '#f1f5f9', padding: '1px 6px', borderRadius: '4px' }}>
                    {m.status}
                  </span>
                }
              />
            </>,
            m.id
          ))}
          <div style={{ padding: '12px 16px', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <input value={newMilestone.name} onChange={(e) => setNewMilestone(p => ({ ...p, name: e.target.value }))}
              placeholder="Milestone name" style={{ ...inputStyle, flex: '1 1 200px' }} />
            <select value={newMilestone.status} onChange={(e) => setNewMilestone(p => ({ ...p, status: e.target.value as Milestone['status'] }))}
              style={inputStyle}>
              {MILESTONE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <Button variant="primary" size="sm" disabled={!newMilestone.name.trim()} loading={createMilestoneMutation.isPending}
              onClick={() => createMilestoneMutation.mutate()}>
              <Plus size={13} /> Add
            </Button>
          </div>
        </>
      )}
      </div>

      {project && (
        <EditProjectModal
          isOpen={editProjectOpen}
          onClose={() => setEditProjectOpen(false)}
          project={project}
          onSave={(data) => updateProjectMutation.mutate(data)}
          isSaving={updateProjectMutation.isPending}
        />
      )}
    </>
  )
}
